/**
 * Deterministic author color from an identity string.
 *
 * MUST stay bit-identical to content-api/content/services/colors.py
 * (Python). Server stamps `author_color` paragraph-attrs at write time;
 * the browser computes the same value for live rendering. We deliberately
 * compute it on the fly in the browser (rather than reading the stored
 * attr) so a palette change applies to ALL posts — old and new — without
 * a migration of every paragraph in every doc.
 *
 * V2.7.5: hand-picked qualitative palette (Tableau-10 inspired).
 *
 * Seit Post #5048 hat eine Registry-Farbe Vorrang (siehe loadAgentColors);
 * dieser Hash ist der Fallback und bleibt bit-gleich zu colors.py.
 * Adjacent slots land on opposite sides of the wheel and/or different
 * lightness brackets. Replaces the V2.7.2 equidistant spacing that
 * clustered too many agents in the green-yellow band.
 */

const KNOWN_AGENTS_SORTED = [
  // Sorted lowercase to match Python's `sorted(..., key=str.lower)`
  'Alexander Popovic',
  'ArkturianClaude',
  'ArkturianCodex',
  'ArkturianGemini',
  'Cloud',
  'CloudV2',
  'Comm',
  'Content',
  'Knowledge',
  'Review',
  'Storage',
]

// Palette slots — H, S, L. Order must match Python's _PALETTE.
const PALETTE: Array<[number, number, number]> = [
  [210, 75, 50],
  [30, 90, 55],
  [125, 55, 40],
  [280, 65, 55],
  [0, 70, 55],
  [195, 70, 45],
  [50, 95, 50],
  [330, 70, 55],
  [160, 50, 40],
  [15, 65, 40],
  [245, 60, 55],
]

function fnv1a32(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

function stripPrefix(id: string): string {
  const idx = id.indexOf(':')
  return (idx >= 0 ? id.slice(idx + 1) : id).trim()
}

/**
 * Post #5048 — Registry-Farbe (cloud-api, Cloud 12be34c). content-api loest
 * sie serverseitig fail-closed auf (nur bei eindeutiger Zuordnung ueber alle
 * Knoten, sonst Namensfarbe) und liefert sie als `author_color` in
 * /api/v1/agents/available. Hier nur eine Map Name -> Farbe; fehlt ein
 * Eintrag (anonym, nicht sichtbar, noch nicht geladen), greift der Hash.
 */
const REGISTRY = new Map<string, string>()
let registryLoad: Promise<void> | null = null

export async function loadAgentColors(): Promise<void> {
  if (registryLoad) return registryLoad
  registryLoad = (async () => {
    try {
      const { getAccessToken } = await import('../auth')
      const { CONTENT_API } = await import('../config')
      const token = getAccessToken()
      if (!token) return
      const r = await fetch(`${CONTENT_API}/api/v1/agents/available`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok) return
      const data = (await r.json()) as { agents?: Array<{ name?: string; author_color?: string }> }
      REGISTRY.clear()
      for (const a of data.agents || []) {
        if (a.name && a.author_color) REGISTRY.set(a.name, a.author_color)
      }
    } catch {
      /* Registry ist optional — Hash bleibt */
    } finally {
      registryLoad = null
    }
  })()
  return registryLoad
}

/**
 * Zugewiesene Farben je Mandant (#2267, Vorschlag #5186 Teil 6): content-api
 * führt eine Farbtabelle Autorenkennung → Farbe. Sie hat Vorrang vor allem
 * anderen; wer keinen Eintrag hat, behält Registry- oder Namensfarbe.
 * Geladen wird sie je Beitrag (hooks/useAuthorColors).
 */
let ASSIGNED: Record<string, string> = {}
const assignedListeners = new Set<() => void>()

export function setAssignedColors(colors: Record<string, string>): void {
  ASSIGNED = colors
  assignedListeners.forEach((fn) => fn())
}

export function subscribeAssignedColors(fn: () => void): () => void {
  assignedListeners.add(fn)
  return () => { assignedListeners.delete(fn) }
}

export function colorFromId(id: string, name?: string | null): string {
  const assigned = ASSIGNED[id]
  if (assigned) return assigned
  if (id.startsWith('agent:')) {
    const reg = REGISTRY.get(stripPrefix(id))
    if (reg) return reg
  }
  // KNOWN-agents palette lookup tries TWO keys:
  //   1) the display name (preferred — covers e.g. user:UUID where the
  //      id itself is opaque but author_name is "Alexander Popovic")
  //   2) the stripped identity-suffix (agent:Cloud → "Cloud")
  // Falling back to FNV-1a hash if neither matches.
  const candidates: string[] = []
  if (name) candidates.push(name.trim())
  candidates.push(stripPrefix(id))
  for (const cand of candidates) {
    const idx = KNOWN_AGENTS_SORTED.indexOf(cand)
    if (idx >= 0) {
      const [h, s, l] = PALETTE[idx % PALETTE.length]
      return `hsl(${h}, ${s}%, ${l}%)`
    }
  }
  const hash = fnv1a32(id)
  const hue = hash % 360
  const lightness = 40 + ((hash >>> 16) % 5) * 5
  const saturation = 60 + ((hash >>> 24) % 3) * 10
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}
